import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { MdContentCopy, MdCheck, MdAdd, MdDelete, MdRefresh, MdEdit, MdClose } from 'react-icons/md';
import { FiExternalLink } from 'react-icons/fi';
import FormContainer from '../../../components/CommonComponents/FormContainer';
import Wrapper from '../../../components/CommonComponents/Wrapper';
import MessageHeading from '../../../components/CommonComponents/MessageHeading';
import MessageLabel from '../../../components/CommonComponents/MessageLabel';
import AdminCustomInput from '../../../components/CommonComponents/AdminCustomInput';
import AdminCustomSelect from '../../../components/CommonComponents/AdminCustomSelect';
import AdminSaveHeader from '../../../components/CommonComponents/AdminSaveHeader';

const SSAI_BASE = process.env.REACT_APP_SSAI_URL || 'http://localhost:3003';
const SSAI_API_KEY = process.env.REACT_APP_SSAI_API_KEY || 'development-api-key-123';

const ssaiHeaders = {
    'Content-Type': 'application/json',
    'x-api-key': SSAI_API_KEY,
};

const AD_TYPE_OPTIONS = [
    { name: 'Pre-Roll', value: 'pre_roll' },
    { name: 'Mid-Roll', value: 'mid_roll' },
    { name: 'Post-Roll', value: 'post_roll' },
    { name: 'Overlay', value: 'overlay' },
];

const TYPE_OPTIONS = [
    { name: 'Linear', value: 'linear' },
    { name: 'Non-Linear', value: 'non_linear' },
    { name: 'Companion', value: 'companion' },
];

const STATUS_OPTIONS = [
    { name: 'Active', value: 'active' },
    { name: 'Paused', value: 'paused' },
    { name: 'Draft', value: 'draft' },
];

const emptyForm = {
    name: '',
    vastTagUrl: '',
    adType: 'pre_roll',
    type: 'linear',
    budget: '',
    status: 'active',
    advertiserId: '',
};

function CopyButton({ text, label = '' }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            toast.success(`${label || 'URL'} copied!`);
            setTimeout(() => setCopied(false), 2000);
        });
    };
    return (
        <button
            onClick={handleCopy}
            title={`Copy ${label}`}
            className="btn btn-sm ms-2 d-inline-flex align-items-center gap-1"
            style={{ background: copied ? '#198754' : '#0d6efd', color: '#fff', borderRadius: 6, padding: '3px 10px', fontSize: 12 }}
        >
            {copied ? <MdCheck size={14} /> : <MdContentCopy size={14} />}
            {copied ? 'Copied' : 'Copy'}
        </button>
    );
}

function UrlRow({ label, url }) {
    return (
        <div className="d-flex align-items-center mb-2 gap-2 flex-wrap">
            <span className="fw-semibold theme-text-color" style={{ minWidth: 80, fontSize: 12 }}>{label}:</span>
            <code
                style={{
                    background: 'rgba(0,0,0,0.15)',
                    borderRadius: 6,
                    padding: '3px 10px',
                    fontSize: 11,
                    wordBreak: 'break-all',
                    flex: 1,
                    color: '#7ee8fa',
                }}
            >
                {url}
            </code>
            <CopyButton text={url} label={label} />
            <a href={url} target="_blank" rel="noreferrer" title="Open in new tab" style={{ color: '#7ee8fa' }}>
                <FiExternalLink size={14} />
            </a>
        </div>
    );
}

function StatusBadge({ status }) {
    const colors = {
        active: { bg: '#d1fae5', color: '#065f46' },
        paused: { bg: '#fef3c7', color: '#92400e' },
        draft: { bg: '#e5e7eb', color: '#374151' },
        completed: { bg: '#dbeafe', color: '#1e40af' },
    };
    const c = colors[status] || colors.draft;
    return (
        <span style={{ background: c.bg, color: c.color, borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>
            {status}
        </span>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// SSAI Performance Analytics Panel
// ─────────────────────────────────────────────────────────────────────────────
function AnalyticsDashboard() {
    const [analytics, setAnalytics] = useState(null);
    const [loadingAnalytics, setLoadingAnalytics] = useState(true);

    const fetchAnalytics = useCallback(async () => {
        setLoadingAnalytics(true);
        try {
            const res = await axios.get(`${SSAI_BASE}/api/advertiser/analytics/summary`, { headers: ssaiHeaders });
            setAnalytics(res.data?.data || null);
        } catch (err) {
            toast.error('Failed to load analytics: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoadingAnalytics(false);
        }
    }, []);

    useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

    if (loadingAnalytics) {
        return (
            <div className="text-center py-5">
                <span className="spinner-border" />
                <p className="mt-2 theme-text-color">Loading analytics…</p>
            </div>
        );
    }

    if (!analytics) {
        return <div className="text-center py-5 theme-text-color opacity-50">No analytics data available.</div>;
    }

    const { summary, topCampaigns, revenueByStatus } = analytics;

    const statCard = (label, value, sub) => (
        <div className="col-6 col-md-3">
            <div className="rounded-3 p-3 text-center"
                style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#a5b4fc' }}>{value}</div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>{label}</div>
                {sub && <div style={{ fontSize: 11, color: '#6b7280' }}>{sub}</div>}
            </div>
        </div>
    );

    return (
        <Wrapper>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <MessageHeading text="SSAI Performance Analytics" />
                <button className="btn btn-sm btn-outline-secondary" onClick={fetchAnalytics}>
                    <MdRefresh size={15} /> Refresh
                </button>
            </div>

            {/* KPI Summary Row */}
            <div className="row g-3 mb-4">
                {statCard('Total Impressions', summary.impressions.total.toLocaleString(), 'all time')}
                {statCard('Completions', summary.completions.toLocaleString(), null)}
                {statCard('Completion Rate', `${summary.completionRate}%`, null)}
                {statCard('Total Revenue', `$${summary.revenue.toLocaleString()}`, null)}
            </div>
            <div className="row g-3 mb-4">
                {statCard('Active Campaigns', summary.activeCampaigns, `of ${summary.totalCampaigns} total`)}
                {statCard('Total Clicks', summary.clicks.toLocaleString(), null)}
                {statCard('Revenue (Active)', `$${revenueByStatus.active}`, null)}
                {statCard('Revenue (Completed)', `$${revenueByStatus.completed}`, null)}
            </div>

            {/* Top Campaigns Table */}
            <div className="mt-2">
                <p className="fw-semibold theme-text-color mb-2" style={{ fontSize: 13 }}>
                    Top Campaigns by Completion Rate
                </p>
                {topCampaigns.length === 0 ? (
                    <div className="text-center py-3 theme-text-color opacity-50" style={{ fontSize: 13 }}>
                        No campaign impressions recorded yet.
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-sm" style={{ fontSize: 12 }}>
                            <thead>
                                <tr style={{ color: '#94a3b8' }}>
                                    <th>Campaign</th>
                                    <th>Type</th>
                                    <th>Status</th>
                                    <th className="text-end">Impressions</th>
                                    <th className="text-end">Completions</th>
                                    <th className="text-end">Completion Rate</th>
                                    <th className="text-end">Revenue</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topCampaigns.map(c => (
                                    <tr key={c.id}>
                                        <td className="theme-text-color">{c.name}</td>
                                        <td className="text-muted">{c.adType?.replace('_', '-')}</td>
                                        <td><StatusBadge status={c.status} /></td>
                                        <td className="text-end theme-text-color">{c.impressions.toLocaleString()}</td>
                                        <td className="text-end theme-text-color">{c.completions.toLocaleString()}</td>
                                        <td className="text-end">
                                            <span style={{
                                                color: c.completionRate >= 75 ? '#34d399' : c.completionRate >= 50 ? '#fbbf24' : '#f87171',
                                                fontWeight: 600
                                            }}>
                                                {c.completionRate}%
                                            </span>
                                        </td>
                                        <td className="text-end theme-text-color">${c.spend.toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </Wrapper>
    );
}

const SSAIAdsManagement = () => {
    const [activeTab, setActiveTab] = useState('campaigns');
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null); // null = create mode, string = edit mode
    const [formData, setFormData] = useState(emptyForm);
    const [formError, setFormError] = useState({});
    const [saving, setSaving] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const fetchCampaigns = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${SSAI_BASE}/api/advertiser/campaigns`, { headers: ssaiHeaders });
            setCampaigns(res.data?.data || []);
        } catch (err) {
            toast.error('Failed to load campaigns: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

    const handleInput = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setFormError(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const err = {};
        if (!formData.name.trim()) err.name = 'Campaign name is required';
        if (!formData.vastTagUrl.trim()) err.vastTagUrl = 'VAST tag URL is required';
        else {
            // Strip ad macro placeholders (e.g. [UA], [AD_ID], [SESSION_ID]) before URL validation
            const stripped = formData.vastTagUrl.trim().replace(/\[.*?\]/g, 'MACRO').replace(/\s+/g, '');
            try { new URL(stripped); } catch { err.vastTagUrl = 'Must be a valid URL (ad macros like [UA] are allowed)'; }
        }
        setFormError(err);
        return Object.keys(err).length === 0;
    };

    const handleCreate = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const payload = {
                name: formData.name,
                vastTagUrl: formData.vastTagUrl,
                adType: formData.adType,
                type: formData.type,
                budget: formData.budget ? parseFloat(formData.budget) : undefined,
                status: formData.status,
                advertiserId: formData.advertiserId || undefined,
            };
            const res = await axios.post(`${SSAI_BASE}/api/advertiser/campaigns`, payload, { headers: ssaiHeaders });
            toast.success('Campaign created successfully!');
            const newCampaign = res.data.data;
            setCampaigns(prev => [newCampaign, ...prev]);
            setFormData(emptyForm);
            setShowForm(false);
            setExpandedId(newCampaign.id); // auto-expand to show generated URLs
        } catch (err) {
            toast.error('Failed to create campaign: ' + (err.response?.data?.message || err.message));
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (campaign) => {
        setFormData({
            name:         campaign.name         || '',
            vastTagUrl:   campaign.vastTagUrl    || '',
            adType:       campaign.adType        || 'pre_roll',
            type:         campaign.type          || 'linear',
            budget:       campaign.budget != null ? String(campaign.budget) : '',
            status:       campaign.status        || 'active',
            advertiserId: campaign.advertiserId  || '',
        });
        setFormError({});
        setEditingId(campaign.id);
        setShowForm(true);
        // Scroll form into view
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleUpdate = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const payload = {
                name:         formData.name,
                vastTagUrl:   formData.vastTagUrl,
                adType:       formData.adType,
                type:         formData.type,
                budget:       formData.budget ? parseFloat(formData.budget) : undefined,
                status:       formData.status,
                advertiserId: formData.advertiserId || undefined,
            };
            const res = await axios.put(
                `${SSAI_BASE}/api/advertiser/campaigns/${editingId}`,
                payload,
                { headers: ssaiHeaders }
            );
            const updated = res.data?.data || { ...payload, id: editingId };
            toast.success('Campaign updated successfully!');
            setCampaigns(prev => prev.map(c => c.id === editingId ? { ...c, ...updated } : c));
            setFormData(emptyForm);
            setEditingId(null);
            setShowForm(false);
        } catch (err) {
            toast.error('Failed to update campaign: ' + (err.response?.data?.message || err.message));
        } finally {
            setSaving(false);
        }
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setEditingId(null);
        setFormData(emptyForm);
        setFormError({});
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this campaign?')) return;
        setDeletingId(id);
        try {
            await axios.delete(`${SSAI_BASE}/api/advertiser/campaigns/${id}`, { headers: ssaiHeaders });
            toast.success('Campaign deleted');
            setCampaigns(prev => prev.filter(c => c.id !== id));
            if (expandedId === id) setExpandedId(null);
        } catch (err) {
            toast.error('Failed to delete: ' + (err.response?.data?.message || err.message));
        } finally {
            setDeletingId(null);
        }
    };

    const vastUrl = (id) => `${SSAI_BASE}/api/advertiser/campaigns/${id}/vast`;
    const vmapUrl = (id) => `${SSAI_BASE}/api/advertiser/campaigns/${id}/vmap`;

    const tabStyle = (tab) => ({
        cursor: 'pointer',
        padding: '8px 20px',
        fontSize: 13,
        fontWeight: activeTab === tab ? 700 : 400,
        borderBottom: activeTab === tab ? '2px solid #6366f1' : '2px solid transparent',
        color: activeTab === tab ? '#a5b4fc' : '#94a3b8',
        background: 'none',
        border: 'none',
        outline: 'none',
    });

    return (
        <FormContainer>
            <AdminSaveHeader
                heading="SSAI Campaign Management"
                saveMethod={() => {
                    setFormData(emptyForm);
                    setFormError({});
                    setEditingId(null);
                    setShowForm(true);
                    setActiveTab('campaigns');
                }}
                saveText="+ New Campaign"
            />

            {/* Tab Navigation */}
            <div className="d-flex gap-1 mb-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <button style={tabStyle('campaigns')} onClick={() => setActiveTab('campaigns')}>
                    Campaigns
                </button>
                <button style={tabStyle('analytics')} onClick={() => setActiveTab('analytics')}>
                    Performance Analytics
                </button>
            </div>

            {activeTab === 'analytics' && <AnalyticsDashboard />}
            {activeTab !== 'analytics' && (<>

            {/* ── Create / Edit Campaign Form ── */}
            {showForm && (
                <Wrapper>
                    <div className="d-flex align-items-center justify-content-between mb-1">
                        <MessageHeading text={editingId ? 'Edit SSAI Campaign' : 'Create New SSAI Campaign'} />
                        <button
                            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                            onClick={handleCloseForm}
                            title="Close form"
                        >
                            <MdClose size={15} /> Close
                        </button>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <MessageLabel text="Campaign Name *" />
                            <AdminCustomInput
                                name="name"
                                value={formData.name}
                                onChange={handleInput}
                                placeHolder="e.g. Q2 Pre-Roll Campaign"
                                error={formError.name}
                            />
                        </div>
                        <div className="col-md-6">
                            <MessageLabel text="Advertiser ID (optional)" />
                            <AdminCustomInput
                                name="advertiserId"
                                value={formData.advertiserId}
                                onChange={handleInput}
                                placeHolder="Advertiser account ID"
                            />
                        </div>
                    </div>

                    <div className="mt-3">
                        <MessageLabel text="Upstream VAST Tag URL *" />
                        <AdminCustomInput
                            name="vastTagUrl"
                            value={formData.vastTagUrl}
                            onChange={handleInput}
                            placeHolder="https://pubads.g.doubleclick.net/gampad/ads?..."
                            error={formError.vastTagUrl}
                        />
                        <small className="text-muted">
                            Your upstream ad network VAST URL. FlickNexs SSAI wraps this to produce the generated VMAP/VAST URLs below.
                        </small>
                    </div>

                    <div className="row mt-3">
                        <div className="col-md-4">
                            <MessageLabel text="Ad Type" />
                            <AdminCustomSelect
                                name="adType"
                                value={formData.adType}
                                onChange={handleInput}
                                options={AD_TYPE_OPTIONS}
                            />
                        </div>
                        <div className="col-md-4">
                            <MessageLabel text="Creative Type" />
                            <AdminCustomSelect
                                name="type"
                                value={formData.type}
                                onChange={handleInput}
                                options={TYPE_OPTIONS}
                            />
                        </div>
                        <div className="col-md-4">
                            <MessageLabel text="Status" />
                            <AdminCustomSelect
                                name="status"
                                value={formData.status}
                                onChange={handleInput}
                                options={STATUS_OPTIONS}
                            />
                        </div>
                    </div>

                    <div className="mt-3 col-md-4">
                        <MessageLabel text="Budget (optional)" />
                        <AdminCustomInput
                            type="number"
                            name="budget"
                            value={formData.budget}
                            onChange={handleInput}
                            placeHolder="e.g. 500"
                        />
                    </div>

                    <div className="d-flex gap-2 mt-4">
                        <button
                            className="btn btn-primary d-flex align-items-center gap-1"
                            onClick={editingId ? handleUpdate : handleCreate}
                            disabled={saving}
                        >
                            {saving ? (
                                <><span className="spinner-border spinner-border-sm" /> Saving...</>
                            ) : editingId ? (
                                <><MdEdit size={18} /> Update Campaign</>
                            ) : (
                                <><MdAdd size={18} /> Create Campaign & Generate URLs</>
                            )}
                        </button>
                        <button className="btn btn-secondary" onClick={handleCloseForm}>
                            Cancel
                        </button>
                    </div>
                </Wrapper>
            )}

            {/* ── Campaign List ── */}
            <Wrapper>
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <MessageHeading text={`SSAI Campaigns (${campaigns.length})`} />
                    <button className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1" onClick={fetchCampaigns}>
                        <MdRefresh size={15} /> Refresh
                    </button>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <span className="spinner-border" />
                        <p className="mt-2 theme-text-color">Loading campaigns…</p>
                    </div>
                ) : campaigns.length === 0 ? (
                    <div className="text-center py-5 theme-text-color opacity-50">
                        <p>No campaigns found. Click <strong>+ New Campaign</strong> to create one.</p>
                    </div>
                ) : (
                    <div className="d-flex flex-column gap-3">
                        {campaigns.map(campaign => (
                            <div
                                key={campaign.id}
                                className="rounded-3 theme-bg-color"
                                style={{ border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden' }}
                            >
                                {/* Campaign header row */}
                                <div
                                    className="d-flex align-items-center justify-content-between p-3"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setExpandedId(expandedId === campaign.id ? null : campaign.id)}
                                >
                                    <div className="d-flex align-items-center gap-3 flex-wrap">
                                        <div>
                                            <span className="fw-bold theme-text-color">{campaign.name}</span>
                                            <span className="ms-2 text-muted" style={{ fontSize: 12 }}>
                                                {campaign.adType?.replace('_', '-')} / {campaign.type}
                                            </span>
                                        </div>
                                        <StatusBadge status={campaign.status} />
                                        {campaign.budget && (
                                            <span className="text-muted" style={{ fontSize: 12 }}>
                                                Budget: ${campaign.budget}
                                            </span>
                                        )}
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <button
                                            className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                                            title="Edit campaign"
                                            onClick={e => { e.stopPropagation(); handleEdit(campaign); }}
                                        >
                                            <MdEdit size={14} /> Edit
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                            disabled={deletingId === campaign.id}
                                            title="Delete campaign"
                                            onClick={e => { e.stopPropagation(); handleDelete(campaign.id); }}
                                        >
                                            <MdDelete size={14} />
                                        </button>
                                        <span className="theme-text-color" style={{ fontSize: 18, opacity: 0.5 }}>
                                            {expandedId === campaign.id ? '▲' : '▼'}
                                        </span>
                                    </div>
                                </div>

                                {/* Expanded: Generated URL panel */}
                                {expandedId === campaign.id && (
                                    <div className="px-3 pb-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                                        <div
                                            className="mt-3 p-3 rounded-3"
                                            style={{ background: 'rgba(0,200,120,0.08)', border: '1px solid rgba(0,200,120,0.2)' }}
                                        >
                                            <p className="mb-2 fw-semibold" style={{ color: '#00c878', fontSize: 13 }}>
                                                ✅ Generated Ad Tag URLs — Copy these into your content's "Custom SSAI VMAP URL" field
                                            </p>

                                            <UrlRow
                                                label="VMAP URL"
                                                url={vmapUrl(campaign.id)}
                                            />
                                            <UrlRow
                                                label="VAST URL"
                                                url={vastUrl(campaign.id)}
                                            />

                                            <div className="mt-3 p-2 rounded-2" style={{ background: 'rgba(255,255,255,0.04)', fontSize: 12 }}>
                                                <span className="fw-semibold theme-text-color">Upstream VAST Tag:</span>
                                                <span className="ms-2 text-muted" style={{ wordBreak: 'break-all' }}>{campaign.vastTagUrl}</span>
                                            </div>

                                            <div className="mt-3 d-flex flex-wrap gap-3" style={{ fontSize: 12, color: '#94a3b8' }}>
                                                <span>📋 ID: <code style={{ color: '#7ee8fa' }}>{campaign.id}</code></span>
                                                <span>📅 Created: {campaign.createdAt ? new Date(campaign.createdAt).toLocaleDateString() : '—'}</span>
                                                <span>👁 Impressions: {campaign.stats?.impressions ?? 0}</span>
                                                <span>🖱 Clicks: {campaign.stats?.clicks ?? 0}</span>
                                            </div>
                                        </div>

                                        <div className="mt-3 p-2 rounded-2" style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', fontSize: 12, color: '#a5b4fc' }}>
                                            <strong>💡 How to use:</strong> Copy the <strong>VMAP URL</strong> above → go to Videos/Series/Episodes/Live Streams →
                                            open Add/Edit form → paste into the <strong>"Custom SSAI VMAP URL"</strong> field → Save.
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </Wrapper>
            </>)}
        </FormContainer>
    );
};

export default SSAIAdsManagement;
